import { gql } from '@apollo/client';
import { DeleteIcon, DownloadIcon, ExternalLinkIcon } from '@assets/icons';
import { CopyIconButton, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { useConfirmation } from '@components/modal';
import { Typography } from '@components/typography';
import { useDeleteAnalyticsMutation, useGetAnalyticForDownloadLazyQuery } from '@generated/graphql';
import { saveFile } from '@helper-functions/save-file';
import { Grid } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import { DatasetsDetailsMicroserviceStatus } from '@subsets/workspaces';
import { navigate } from 'gatsby';
import { MouseEvent } from 'react';

gql`
  query GetAnalyticForDownload($workspaceId: String!, $analyticsId: String!) {
    getAnalytics(workspaceId: $workspaceId, analyticsId: $analyticsId) {
      analyticsId
      dataset
      result
    }
  }
`;

interface DatasetsDetailsAnalyticsListItemPreviewProps {
  workspaceId: string;
  status: string;
  datasetId: string;
  analyticsId: string;
  type: string;
  refetch: () => void;
}

export const DatasetsDetailsAnalyticsListItemPreview = ({
  workspaceId,
  status,
  analyticsId,
  datasetId,
  type,
  refetch,
}: DatasetsDetailsAnalyticsListItemPreviewProps) => {
  const { organizationId } = useOrganizationContext();
  const { createAsyncNotification, addNotification, removeNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [getAnalytics] = useGetAnalyticForDownloadLazyQuery({
    variables: { analyticsId, workspaceId },
    fetchPolicy: 'network-only',
  });
  const [deleteAnalyticsMutation] = useDeleteAnalyticsMutation();

  const handleDownloadDatasetAnalytics = async () => {
    const key = `${Date.now() * 1000}.${Math.random() * 10000}`;
    try {
      addNotification({ text: 'Loading...', type: 'loading', key, duration: 100000 });
      const {
        data: { getAnalytics: analyticsData },
      } = await getAnalytics();
      if (!analyticsData?.[0]) {
        return;
      }
      const href = `data:application/json;charset=utf-8;,${encodeURIComponent(
        analyticsData?.[0]?.result,
      )}`;
      saveFile({ url: href, name: `${analyticsData?.[0]?.dataset}_${type}.json` });
      removeNotification(key);
      addNotification({
        text: 'Starting download...',
        type: 'success',
        duration: 6000,
      });
    } catch {
      removeNotification(key);
    }
  };

  const handleDeleteDatasetAnalytics = () =>
    showConfirmation({
      onAffirm: createAsyncNotification('Successfully deleted analytics.', async () => {
        await deleteAnalyticsMutation({
          variables: {
            workspaceId,
            analyticsId,
          },
        });
        void refetch();
      }),
      message: `Are you sure you want to delete this analytics?`,
    });

  const handleSelectAnalytic = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    void navigate(
      `/${organizationId}/workspaces/${workspaceId}/experiments/?tab=analytics&analyticsId=${analyticsId}`,
    );
  };

  return (
    <Grid container alignItems="center" justifyContent="flex-start">
      <Grid item xs={1.5}>
        <Group marginLeft={1}>
          <DatasetsDetailsMicroserviceStatus
            status={status}
            microServiceName="analytics"
            workspaceId={workspaceId}
            datasetId={datasetId}
            microServiceId={analyticsId}
          />
        </Group>
      </Grid>
      <Grid item xs={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
        <CopyIconButton value={analyticsId} showValue tooltipProps={{ variant: 'secondary' }} />
      </Grid>
      <Grid item xs={5}>
        <Typography handleOverFlow variant="body2">
          {type}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Group justifyContent="flex-end" gap={2}>
          {status === 'success' && (
            <>
              <IconButton onClick={handleSelectAnalytic} Icon={ExternalLinkIcon} />
              <IconButton onClick={handleDownloadDatasetAnalytics} Icon={DownloadIcon} />
            </>
          )}
          <IconButton onClick={handleDeleteDatasetAnalytics} Icon={DeleteIcon} />
        </Group>
      </Grid>
    </Grid>
  );
};
